import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Truck, CheckCircle2 } from "lucide-react";

export default function Signup() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [companyName, setCompanyName] = useState("");
  const [username, setUsername] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!adminEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail.trim())) {
      toast.error("A valid admin email is required — your drivers reply to it");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/signup", {
        company_name: companyName.trim(),
        admin_username: username.trim().toLowerCase(),
        admin_password: password,
        admin_email: adminEmail.trim().toLowerCase(),
      });
      setDone(true);
      toast.success("Workspace created — awaiting approval");
    } catch (err) {
      toast.error(errMsg(err, "Signup failed"));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-slate-50">
        <div className="w-full max-w-md bg-white rounded-lg border border-slate-200 shadow-sm p-8 text-center" data-testid="signup-pending">
          <CheckCircle2 className="mx-auto text-emerald-600" size={48} />
          <h1 className="mt-4 text-xl font-semibold text-slate-900">Workspace created</h1>
          <p className="mt-2 text-sm text-slate-600">
            Your 14-day trial starts the moment the platform owner approves your workspace. You'll be able to sign in as <strong className="font-mono">{username}</strong> after that.
          </p>
          <Button
            className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => nav("/login")}
            data-testid="back-to-login-btn"
          >
            Back to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-center p-16 bg-[#0B1A3A] border-r border-slate-800 text-white">
        <img
          src="/dispatch-pay-logo.png"
          alt="Dispatch Pay Calculator"
          className="w-full max-w-md h-auto mb-10"
        />
        <h1 className="text-4xl font-semibold leading-tight">
          Everything your fleet needs for driver pay, in one clean app.
        </h1>
        <ul className="mt-6 space-y-3 text-sm text-slate-300 max-w-md">
          {[
            "Trip entry, per-mile and percentage pay with team splits",
            "One-click PDF statements with your logo and company header",
            "QuickBooks CSV export for every pay period",
            "Custom fields for drivers and trips",
            "Audit trail on every change",
          ].map((t) => (
            <li key={t} className="flex gap-2">
              <CheckCircle2 size={16} className="text-amber-400 mt-0.5 shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-16">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <div className="font-semibold text-slate-900 mb-5">Create your workspace</div>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Company name</Label>
                <Input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Acme Trucking LLC"
                  required
                  data-testid="signup-company"
                />
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Admin username</Label>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="jsmith"
                  required
                  data-testid="signup-username"
                />
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Admin email <span className="text-rose-500">*</span></Label>
                <Input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="payroll@acmetrucking.com"
                  required
                  data-testid="signup-email"
                />
                <div className="text-[11px] text-slate-500 mt-1">We&rsquo;ll use this as the reply address on every email your drivers get. They hit &ldquo;Reply&rdquo; and it comes straight to you.</div>
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Password (min 6 chars)</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="signup-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={loading}
                data-testid="signup-submit"
              >
                {loading ? "Creating..." : "Create workspace"}
              </Button>
            </form>
            <div className="text-[11px] text-slate-500 mt-4">
              New workspaces are reviewed before access is granted. You'll be able to sign in once approved.
            </div>
          </div>
          <div className="mt-5 text-center text-xs text-slate-500">
            Already have an account? <Link to="/login" className="text-blue-700 font-semibold">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
